<template>
  <div>
    <div class="mb-20 flex items-center justify-between">
      <div>
        <h2
          class="text-neutral-text-strong font-bold text-lg"
          data-testid="title"
        >
          Custom Populations
        </h2>
        <p class="text-neutral-text" data-testid="subtitle">
          Create custom populations for specific segments of your Customers,
          Prospects, Open Opportunities, or for other groups — and share them
          with partners when needed
        </p>
      </div>
      <component
        :is="divOrUpsell"
        :button-text="requiresSupernodeUpgrade ? 'Talk to Sales' : 'Upgrade'"
        :hide-upsell="!canCreatePopulations"
        :billing-interaction="{
          cta: POPULATION_CTAS.CUSTOM_POPULATIONS,
          event_site: EVENT_SITES.CUSTOM_POPULATION_RESTRICTION,
          talkToSalesReason: requiresSupernodeUpgrade
            ? 'Custom Populations'
            : undefined,
        }"
      >
        <BittsButton
          v-if="!isFreeTier"
          :left-icon="['fak', 'add']"
          text="Create Population"
          size="small"
          :disabled="
            disabled ||
            (isConnectorTier && atConnectorLimit) ||
            !canCreatePopulations
          "
          variant="outline"
          class="ml-auto"
          @click="createCustomPopulation"
        />
        <template #title>
          {{
            requiresSupernodeUpgrade
              ? 'Unlock unlimited Custom Populations on Supernode'
              : 'Create custom segments tailored to your data'
          }}
        </template>
      </component>
    </div>
    <div
      v-if="isFreeTier || hasExpiredConnectorTrial"
      class="c-custom-populations__pops"
      :class="{ 'c-custom-populations__pops--has-open-data': hasOpenData }"
    >
      <PopulationItem
        v-for="pop in openDataPopulations"
        :key="`od-${pop.id}`"
        :population="pop"
        :at-or-above-cap="false"
        :is-offline-partner="isOfflinePartner"
        :offline-partner-uuid="offlinePartnerUuid"
        @pop-clicked="emit('population-clicked', pop)"
        @view-population="emit('view-population', pop)"
      />
      <PopulationItem
        v-if="firstNonOpenDataPopulation"
        :population="firstNonOpenDataPopulation"
        :at-or-above-cap="true"
        :is-offline-partner="isOfflinePartner"
        :offline-partner-uuid="offlinePartnerUuid"
      />
      <CustomPopSkeleton
        v-for="(_, index) in Array.from(Array(skeletonPopulationCount))"
        :key="`skeleton-${index}`"
        :is-last="index === skeletonPopulationCount - 1"
        :is-first="!firstNonOpenDataPopulation && index === 0"
      />
      <!-- This overlay wraps + constrains the custom pops if it's a free account -->
      <div
        v-if="
          (isFreeTier && !hasUnlimitedCustomPops) || hasExpiredConnectorTrial
        "
        class="restriction-wrapper"
        data-testid="restriction-wrapper"
      >
        <FontAwesomeIcon
          :icon="['fak', 'population']"
          class="text-upsell-accent text-40"
        />
        <h3>{{ cta.title }}</h3>
        <p>{{ cta.description }}</p>
        <BittsButton
          v-if="canCreatePopulations"
          data-testid="restriction-wrapper-button"
          size="small"
          :text="requiresSupernodeUpgrade ? 'Talk to Sales' : 'Upgrade'"
          type="upsell"
          :left-icon="['fak', 'upgrade']"
          @click="handleRestrictedClick"
        />
      </div>
    </div>
    <div v-else-if="populations.length">
      <div class="mb-20 flex justify-between items-center">
        <BittsSegment
          :tabs
          @change:segment="(tab: TabId) => (activeTab = tab as TabId)"
        />
        <BittsInput
          v-model="searchQuery"
          name="Custom Pop Search"
          type="search"
          size="small"
          class="w-[250px] ml-auto"
          placeholder="Search for populations"
        />
      </div>
      <div v-if="filteredPopulations.length" class="overflow-x-auto">
        <ListContainer
          :list="filteredPopulations"
          :get-key="(population: Population) => population.id"
          :has-divider="() => false"
          :show-hover="true"
          class="c-list-container--new-modal min-w-[640px]"
        >
          <template #header>
            <div
              class="border-b border-solid border-neutral-border bg-neutral-background-weak rounded-tr-16 rounded-tl-16 px-4 py-8 flex items-center w-full"
            >
              <span class="text-sm font-bold text-neutral-text px-12 flex-1">
                Population Name
              </span>
              <span
                v-if="!isOfflinePartner"
                class="text-sm font-bold text-neutral-text px-12 w-[220px] shrink-0"
              >
                Sharing Setting
              </span>
              <span
                class="text-sm font-bold text-neutral-text px-12 w-[220px] shrink-0"
              >
                Records Count
              </span>
            </div>
          </template>
          <template #default="{ item: population }">
            <PopulationItem
              :population
              :at-or-above-cap
              :is-offline-partner="isOfflinePartner"
              :offline-partner-uuid="offlinePartnerUuid"
              :show-tag="activeTab === ALL"
              @pop-clicked="emit('population-clicked', population)"
              @create-population="emit('create-population', population)"
              @edit-population="emit('edit-population', population)"
              @view-population="emit('view-population', population)"
            />
          </template>
        </ListContainer>
      </div>
      <BittsCard v-else class="w-full h-[400px]">
        <div class="filtered-empty-state">
          <div class="search-background">
            <FontAwesomeIcon
              :icon="['fak', 'search']"
              :style="{ height: '52px', width: '52px' }"
              class="text-neutral-text-weak mx-auto w-full"
            />
          </div>
          <h3 class="text-neutral-text-strong text-lg font-bold">
            No results
          </h3>
          <p class="text-neutral-text">
            Try a different search term to find your custom populations
          </p>
        </div>
      </BittsCard>
    </div>
    <BittsEmptyState
      v-else
      data-testid="empty-state"
      :title="cta.title"
      :description="cta.description"
      :font-awesome-icon="['fak', 'population']"
      font-awesome-icon-class="text-neutral-accent w-52"
      :button="{
        disabled: !canCreatePopulations,
        text: 'Create Custom Population',
        size: 'small',
      }"
      @button-clicked="createCustomPopulation"
    />
  </div>
</template>
<script setup lang="ts">
import {
  BittsButton,
  BittsCard,
  BittsEmptyState,
  BittsInput,
  BittsSegment,
} from '@crossbeam/bitts';
import { EVENT_SITES, POPULATION_CTAS } from '@crossbeam/itly';

import { storeToRefs } from 'pinia';
import { computed, ref } from 'vue';

import UpsellTooltip from '@/components/billing/UpsellTooltip.vue';
import ListContainer from '@/components/ListContainer.vue';
import CustomPopSkeleton from '@/components/populations/CustomPopSkeleton.vue';
import PopulationItem from '@/components/populations/PopulationItem.vue';

import useAuth from '@/composables/useAuth';
import useBilling from '@/composables/useBilling';
import { useConnectorTrialEndDate } from '@/composables/useConnectorTrialEndDate';
import useIteratively from '@/composables/useIteratively';
import { TEMP_PP_UNLIMITED_CUSTOM } from '@/constants/feature_flags';
import { useBillingStore, useFeatureFlagStore } from '@/stores';
import {
  CUSTOMERS_STANDARD_TYPE,
  OPEN_OPPS_STANDARD_TYPE,
  PROSPECTS_STANDARD_TYPE,
  Population,
  PopulationStats,
  isOpenDataPopulation,
} from '@/types/populations';

const {
  populations = [],
  disabled = true,
  atOrAboveCap,
  isOfflinePartner = false,
  offlinePartnerUuid,
} = defineProps<{
  populations?: (Population & { meta?: PopulationStats })[];
  disabled?: boolean;
  atOrAboveCap: boolean;
  isOfflinePartner?: boolean;
  offlinePartnerUuid?: string;
}>();

const emit = defineEmits<{
  'population-clicked': [population: Population | null];
  'create-population': [population: Population | null];
  'edit-population': [population: Population | null];
  'view-population': [population: Population | null];
}>();

const CONNECTOR_LIMIT = 3;

const billingStore = useBillingStore();
const { iteratively } = useIteratively();

const ALL = 'all';
const OTHER = 'other';

type TabId =
  | typeof ALL
  | typeof CUSTOMERS_STANDARD_TYPE
  | typeof PROSPECTS_STANDARD_TYPE
  | typeof OPEN_OPPS_STANDARD_TYPE
  | typeof OTHER;

const searchQuery = ref('');
const activeTab = ref<TabId>(ALL);
const filteredPopulations = computed<Population[]>(() => {
  if (!searchQuery.value) {
    return populationMap.value[activeTab.value];
  }

  const query = searchQuery.value.toLowerCase();
  return populations.filter(
    (pop) => pop.name.toLowerCase().includes(query) ?? false,
  );
});

const populationMap = computed<Record<TabId, Population[]>>(() => ({
  [ALL]: populations,
  [CUSTOMERS_STANDARD_TYPE]: populations.filter(
    (pop) => pop.segment === CUSTOMERS_STANDARD_TYPE,
  ),
  [PROSPECTS_STANDARD_TYPE]: populations.filter(
    (pop) => pop.segment === PROSPECTS_STANDARD_TYPE,
  ),
  [OPEN_OPPS_STANDARD_TYPE]: populations.filter(
    (pop) => pop.segment === OPEN_OPPS_STANDARD_TYPE,
  ),
  [OTHER]: populations.filter((pop) => pop.segment === OTHER),
}));

const tabs = computed(() => [
  { title: 'All', id: ALL, disabled: false },
  {
    title: 'Customers',
    id: CUSTOMERS_STANDARD_TYPE,
    disabled: !populationMap.value.customers.length,
  },
  {
    title: 'Open Opportunities',
    id: OPEN_OPPS_STANDARD_TYPE,
    disabled: !populationMap.value.open_opportunities.length,
  },
  {
    title: 'Prospects',
    id: PROSPECTS_STANDARD_TYPE,
    disabled: !populationMap.value.prospects.length,
  },
  {
    title: 'Other',
    id: OTHER,
    disabled: !populationMap.value.other.length,
  },
]);

const { isTrialOver } = useConnectorTrialEndDate();

const hasExpiredConnectorTrial = computed(() => {
  return isConnectorTier && isTrialOver.value;
});
const { handleBillingInteraction } = useBilling();
const { hasScope } = useAuth();
const featureFlagStore = useFeatureFlagStore();
const { isFreeTier, isConnectorTier, isSupernodeOrEnterpriseTier } =
  storeToRefs(billingStore);

const canManagePopulations = computed(() => hasScope('write:populations'));
const canManageOfflinePartners = computed(
  () => isOfflinePartner && hasScope('write:offline-partners'),
);

const canCreatePopulations = computed(() => {
  if (isOfflinePartner) {
    return canManageOfflinePartners.value;
  }
  return canManagePopulations.value;
});

function createCustomPopulation() {
  iteratively.userClickedCreatePopulation({
    event_site: EVENT_SITES.CUSTOM_POPULATION_CARD,
    population_type: 'custom',
  });
  emit('create-population', null);
}

const divOrUpsell = computed(() =>
  isFreeTier.value ||
  (isConnectorTier.value && (hasExpiredConnectorTrial.value || atOrAboveCap))
    ? UpsellTooltip
    : 'div',
);

/* This is a temporary feature flag that gives some accounts access to unlimited custom populations. */
const hasUnlimitedCustomPops = computed(() =>
  featureFlagStore.hasFeatureFlag(TEMP_PP_UNLIMITED_CUSTOM),
);

/* The option to create a custom population can be disabled if the user doesn't have permission,
or if they are at their limit for connector accounts */
const atConnectorLimit = computed(
  () =>
    customPopulations.value.length >= CONNECTOR_LIMIT &&
    !hasUnlimitedCustomPops.value,
);

/* CTAs */
const requiresSupernodeUpgrade = computed(
  () => atConnectorLimit.value || hasExpiredConnectorTrial.value,
);

const isRestricted = computed(
  () =>
    isFreeTier.value &&
    !!customPopulations.value.length &&
    !hasUnlimitedCustomPops.value,
);

const allowedCustomPops = computed(() => {
  if (isSupernodeOrEnterpriseTier.value || hasUnlimitedCustomPops.value)
    return 'unlimited';
  return CONNECTOR_LIMIT;
});

const cta = computed(() => {
  return {
    title: isRestricted.value
      ? "Don't lose your Custom Populations"
      : 'Organize and segment target accounts with Custom Populations',
    description: isRestricted.value
      ? 'Upgrade to re-activate your Custom Populations for use with your partners'
      : isFreeTier.value || hasExpiredConnectorTrial.value
        ? 'Target the key stages in your funnel by creating custom segments of your data to compare with your partners'
        : `Your plan includes ${allowedCustomPops.value} custom segments, what are you waiting for?`,
  };
});

function handleRestrictedClick() {
  handleBillingInteraction({
    cta: POPULATION_CTAS.CUSTOM_POPULATIONS,
    event_site: EVENT_SITES.CUSTOM_POPULATION_RESTRICTION,
    talkToSalesReason: requiresSupernodeUpgrade.value
      ? 'Custom Populations'
      : undefined,
  });
}

const openDataPopulations = computed(() =>
  populations.filter(isOpenDataPopulation),
);

const customPopulations = computed(() =>
  populations.filter((pop) => !isOpenDataPopulation(pop)),
);

const hasOpenData = computed(() => !!openDataPopulations.value.length);

const firstNonOpenDataPopulation = computed(() =>
  populations.find((pop) => !isOpenDataPopulation(pop)),
);

const skeletonPopulationCount = computed(() => {
  if (hasOpenData.value && !customPopulations.value.length) return 0;
  if (
    (isFreeTier.value && !hasUnlimitedCustomPops.value) ||
    hasExpiredConnectorTrial.value
  )
    return 5;
  return 0;
});
</script>
<style lang="pcss" scoped>
@reference "../../css/theme.css";
.filtered-empty-state {
  @apply flex flex-col items-center justify-center px-24 py-96;
  .search-background {
    @apply rounded-full w-120 h-120 p-24 bg-white bg-top-gradient from-neutral-accent/20 to-neutral-accent/0;
  }
}
.subtitle {
  @apply text-neutral-text-strong text-lg font-bold mt-40;
}

.subtitle-description {
  @apply text-neutral-text mb-24 flex-1;
}

.c-custom-populations__pops {
  @apply relative flex flex-col;

  :deep(.c-population-item) {
    @apply p-16 h-80 border rounded-t-12 border-neutral-border;
  }

  .c-bitts-empty-state-large {
    @apply w-auto;
  }

  .restriction-wrapper {
    @apply absolute top-0 h-full min-h-[315px] bg-black/50 w-full rounded-lg flex flex-col justify-center items-center px-24 z-10;
    backdrop-filter: blur(0.5px);
    background-image: linear-gradient(
      to top,
      theme(colors.neutral-background),
      color-mix(in srgb, theme(colors.white) 50%, transparent)
    );

    h3 {
      @apply text-neutral-text-strong text-lg font-bold mt-24 text-center;
    }

    p {
      @apply text-neutral-text mb-24 text-center mt-4;
    }
    .bitts-cta-upsell {
      @apply bg-none;
    }
  }

  &.c-custom-populations__pops--has-open-data {
    @apply border border-neutral-border rounded-bts-md overflow-clip;
    box-shadow: 0 2px 3px 0 rgba(13, 25, 42, 0.05);

    :deep(.c-population-item) {
      @apply border-0 rounded-none border-b border-neutral-border/50 cursor-pointer;

      &:hover {
        @apply bg-neutral-background-weak;
      }
    }

    .restriction-wrapper {
      @apply static min-h-0 h-auto rounded-none p-40 bg-transparent;
      backdrop-filter: blur(2px);
      background-image: linear-gradient(
        to bottom,
        rgba(255, 255, 255, 0),
        theme(colors.neutral-background-weak) 64%
      );
    }
  }
}
.c-list-container--new-modal {
  :deep(.item--first) {
    @apply rounded-tr-none rounded-tl-none;
  }
  :deep(.c-bitts-divider) {
    border-color: color-mix(
      in srgb,
      theme(colors.neutral-border) 50%,
      transparent
    );
  }
}
:deep(.c-bitts-empty-state-large-border) {
  @apply bg-neutral-background-weak;
}
</style>
